// Auto-generated from /schemas/configs/ — do not edit manually.
// Regenerate with: cd schemas && just codegen

export interface AgentsAgentsValue {
    /** Short description shown in the backoffice agent card */
    description?: string;
    /** Display name shown in the backoffice */
    name: string;
    /** Lifecycle status of the agent */
    status: 'active' | 'beta' | 'preview' | 'coming_soon';
    /** Whether this agent is enabled for the domain */
    enabled: boolean;
}

export interface AgentsConfig {
    /** Map of agent ID to its definition and enabled state */
    agents?: Record<string, AgentsAgentsValue>;
}

export interface BackofficeConfig {
    /** When disabled, conversations appear in their original language. You can still toggle translation per conversation. */
    auto_translate_conversations?: boolean;
}

export interface CompetitorsProfilesValue {
    /** The competitor's website domain, for example intercom.com. */
    domain?: string;
    /** Alternative: buyers compare you head to head. Replaced: buyers switch from it to you. */
    relation?: 'alternative' | 'replaced';
    /** One or two sentences on what the competitor sells and to whom. */
    summary?: string;
    /** Where you win and where they win. Example: we qualify visitors in the conversation; they focus on support tickets. */
    positioning?: string;
    /** Pages that back up the summary and the comparison, such as a comparison page or a case study. */
    sources?: string[];
}

export interface CompetitorsConfig {
    /** Canonical competitor names the agent recognizes. The agent uses these exact spellings when it speaks about each competitor. Type or paste a comma-separated list (for example: Stripe Billing, Exact Online, Sage Intacct) — entries split on commas. Leave empty to keep the generic defer behavior. */
    names?: string[];
    /** What you know about each competitor, keyed by its name in Known competitors. A profile whose name is not in that list is ignored. */
    profiles?: Record<string, CompetitorsProfilesValue>;
    /** On by default. The agent answers competitor questions in an assertive, sales-rep voice, grounded in whatever your knowledge base holds on that competitor — battlecards, comparison pages, sales notes. When it finds nothing on the competitor it defers instead of guessing. Turn this off to always stay neutral and defer to the competitor on their own product. */
    battlecard_mode?: boolean;
}

export interface IdentityConfig {
    /** Company description for LLM context */
    description?: string;
    /** Company name for display */
    company_name: string;
    /** Main website URL */
    website_url?: string;
    /** Logo URL */
    logo_url?: string | null;
    /** Display name (if different from company_name) */
    display_name?: string;
    /** Buttons, CTAs, and welcome messages always appear in this language. */
    default_language?: 'en' | 'fr' | 'es' | 'de' | 'it' | 'pt' | 'bg' | 'nl' | 'ro' | 'hu';
    /** Website language: AI responds in the site language above (recommended). Auto: AI detects the visitor's language from their message — less reliable, may produce mixed-language answers. */
    llm_response_language?: 'website_language' | 'auto';
    /** Other languages your site serves besides the primary language. Rose shows buttons, CTAs, and starter questions in each of these too — for example, you set a CTA link per language. */
    additional_languages?: string[];
}

export type AgentHttpConfig = Record<string, never>;

export interface AiSectionsConfig {
    /** Show AI Sections on mobile devices */
    enable_mobile?: boolean;
}

export interface AnalyticsTaxonomyTopicsItem {
    /** Description to help LLM identify this topic */
    description: string;
    /** Unique topic identifier (e.g., 'pricing', 'features') */
    id: string;
    /** Human-readable topic name (e.g., 'Pricing & Costs') */
    name: string;
    /** Example questions/phrases for this topic */
    examples?: string[];
}

export interface AnalyticsTaxonomy {
    /** Topic definitions for conversation classification */
    topics?: AnalyticsTaxonomyTopicsItem[];
}

export interface AnalyticsConversionFilters {
    /** Only forms whose HTML id attribute is in this list count as conversions. Leave empty to track every form on form_tracking_pages. Example: [demo_form] tracks only the form with id=demo_form and ignores other forms on the same page (e.g. newsletter signup). */
    form_id_whitelist?: string[];
    /** Only forms that POST to one of these URLs count as conversions. Leave empty to skip this check. Use when conversion forms share a submit endpoint but the page hosts other forms too (e.g. only forms posting to https://start.example.com/users count). */
    form_action_whitelist?: string[];
    /** The page URL must contain one of these text fragments for the submission to count. Leave empty to skip this check. Example: [/sign_up] only counts forms submitted from a sign-up page, ignoring logins or event registrations on other URLs. */
    url_include?: string[];
    /** Submissions from a page URL containing any of these text fragments never count, even when the other filters pass. Example: [capital_deposit] drops a deposit form that lives on an otherwise-tracked sign-up flow. */
    url_exclude?: string[];
}

export interface AnalyticsConversionLabelsItem {
    /** Name shown in analytics, for example Signup, Demo, Webinar, or Contact sales. */
    label: string;
    /** Keep on for real conversions (Signup, Demo). Turn off to tag submissions you do not want counted as conversions, such as newsletter or event-registration forms. They still appear in the breakdown under Excluded, but are removed from your Conversions total. */
    count_as_conversion?: boolean;
    /** Exact submitted form IDs to assign to this label. Pick IDs observed below or paste one from your tracking event. */
    form_ids?: string[];
    /** Thank-you or confirmation page URL patterns for conversions without a form ID. Use * as a wildcard. */
    page_url_patterns?: string[];
    /** Optional conversion detection methods, for example thankyou_page_detected or post_message. Combine with a URL pattern when possible. */
    detection_methods?: string[];
}

export interface AnalyticsParentWindowEvents {
    /** Enable event forwarding to parent window */
    enabled?: boolean;
}

export interface AnalyticsFormDetectionStrategies {
    /** Detects Webflow Superform multi-step forms. Tracks completion when the final step becomes visible (sf:complete event or step9 attribute). */
    superform?: boolean;
    /** Detects HubSpot embedded forms (hsfc-Form, hs-form classes). These React-based forms don't fire native submit events — detection uses DOM replacement observation. */
    hubspot?: boolean;
    /** Tracks standard HTML <form> submissions on pages matching form_tracking_pages patterns. Listens for submit events and detects success/failure via page heuristics. */
    cta_page_form?: boolean;
    /** Fires a conversion event when the visitor lands on a URL matching thank_you_pages patterns. No form element required — works purely on URL matching. */
    thankyou_page?: boolean;
    /** Detects form submissions on SPA pages that use input fields without a <form> element (Angular, React, Vue). Intercepts fetch/XHR POST requests triggered after a submit-button click. */
    network_observer?: boolean;
    /** Detects form submissions by watching for the email input to disappear from the DOM after a submit button click. Designed for SPA multi-step forms where step transitions remove inputs without a backend POST. Disabled by default. */
    dom_observer?: boolean;
    /** EXPERIMENTAL — not finished, does not yet fire conversions reliably. Prefer thank_you_pages when the form redirects to a success URL. Requires dom_observer. By default the DOM observer only watches inputs that sit OUTSIDE a form element. This option also treats inputs inside a submit-less form as trackable and watches an external submit button (e.g. the PayFit welcome funnel shape). Known gaps on multi-step SPA funnels: the success heuristic (email input disappears) misfires, and step-navigation buttons can be picked up. Leave off unless a diagnosis shows this exact structure AND there is no success URL to use with thank_you_pages. */
    dom_observer_track_wrapped_forms?: boolean;
    /** Detects form submissions from cross-origin iframe embeds (HubSpot, Cal.com, Typeform) by listening for window.postMessage events. These providers fire events when forms are submitted inside their iframes. */
    post_message?: boolean;
    /** Detects form submissions via window.dataLayer.push events (Google Tag Manager). Catches HubSpot's standard 'hubspot-form-success' event used by the new embed-components-app iframe, and any custom GTM events listed in dataLayer_conversion_events. */
    data_layer?: boolean;
}

export interface AnalyticsPostRequestCapture {
    /** Enable POST request capture discovery on matching pages */
    enabled?: boolean;
    /** Redact captured payload values before sending them to internal POST discovery storage */
    redact_payload_values?: boolean;
    /** Page URL patterns where POST discovery should run. To match query params or hashes, include ? or # explicitly in the pattern. */
    page_patterns?: string[];
}

export interface AnalyticsInternalIpFilterExcludedIpsItem {
    /** Short name to remember this IP (e.g. 'Paris office'). */
    label: string;
    /** IPv4 or IPv6 address. Paste the IP exactly as it appears (e.g. 203.0.113.42 or 2001:db8::1). */
    ip: string;
}

export interface AnalyticsInternalIpFilter {
    /** When on, visits from the IPs below are excluded from all dashboard stats. Turn off to temporarily include them again without losing the list. */
    enabled?: boolean;
    /** One IP per row. Add a label so you remember what each one is (e.g. 'Paris office', 'Johary home VPN'). IPv4 and IPv6 are both accepted. For ranges, list each IP individually. */
    excluded_ips?: AnalyticsInternalIpFilterExcludedIpsItem[];
}

export interface AnalyticsConfig {
    /** Custom UTM parameter name for session tracking */
    session_utm_name?: string | null;
    /** Custom UTM parameter name for visitor tracking */
    visitor_utm_name?: string | null;
    /** URLs that indicate a logged-in user */
    login_urls?: string[];
    /** Topic classification taxonomy */
    taxonomy?: AnalyticsTaxonomy;
    /** URL patterns of pages with forms to track. To match query params or hashes, include ? or # explicitly in the pattern. */
    form_tracking_pages?: string[];
    /** URL patterns for thank-you/confirmation pages (conversion tracking). To match query params or hashes, include ? or # explicitly in the pattern. */
    thank_you_pages?: string[];
    /** Restricts which detected form submissions count as Rose conversions. Without filters, every form on form_tracking_pages fires rw_client_form_submitted — including newsletter or search forms on the same page. Each filter applies whenever the submission carries the value it checks: a form embedded through HubSpot, Typeform, Calendly or your data layer reports the provider's own form id, so that id has to be in form_id_whitelist for the submission to count. Detections that identify no specific form — thank-you pages, and submissions Rose infers from page or network activity — are matched on url_include / url_exclude only. */
    conversion_filters?: AnalyticsConversionFilters;
    /** Rules are checked in order. The first matching label is used for each conversion event. */
    conversion_labels?: AnalyticsConversionLabelsItem[];
    /** Mark which pages count as reaching a key intent (e.g. your pricing page). Experiments use this to measure how many visitors reach these pages — a much faster signal than waiting for form submissions. Give each intent a name and list the page patterns under it. Patterns match the page path only (the part after your domain); query strings are ignored. Use * as a wildcard. Example: a 'pricing' intent with the patterns /pricing, /pricing/*, /tarifs matches your pricing page, any page beneath it, and your French pricing page. */
    intent_pages?: Record<string, string[]>;
    /** Backoffice metric calculation start date for this domain. Use this when a tracking config change (form_detection_strategies, form_tracking_pages, thank_you_pages, conversion_filters) was made recently and pre-fix events are still polluting the 30-day MV window. Pre-fix events are excluded from engagement / conversion / demo / email-only counts. Leave empty to use the full 30-day window. Format: ISO 8601 timestamp (e.g. 2026-04-20T07:38:52Z). */
    stats_valid_from?: string | null;
    /** URL to the PostHog dashboard for this site (shown as a shortcut link in the backoffice) */
    posthog_url?: string | null;
    /** Controls which analytics events are skipped on pages the widget is not allowed to display on. Tracking of CTA clicks and form submissions is never affected. off: emit everything (current behaviour). init_and_pageleave: skip the widget init and page-exit events on those pages, keep the page view. out_of_scope_hosts: additionally skip the page view on subdomains that appear nowhere in Display Only on URLs (for example a logged-in product app), unless the page is used for form or CTA tracking. */
    page_load_event_suppression?: 'off' | 'init_and_pageleave' | 'out_of_scope_hosts';
    /** Event forwarding to parent window (for analytics integration) */
    parent_window_events?: AnalyticsParentWindowEvents;
    /** Toggle individual form detection strategies on/off per client */
    form_detection_strategies?: AnalyticsFormDetectionStrategies;
    /** Additional GTM dataLayer event names that signal a successful conversion. The built-in 'hubspot-form-success' is always matched. Entries may be a plain event name (e.g. 'contact-form-success', 'lead_MQL') or an object {event, where} that additionally requires every key in 'where' to equal the corresponding property on the pushed dataLayer object — use this when one event name is reused for multiple conversions and only a property like 'eventAction' distinguishes them (e.g. {event: 'formSubmissionSuccess', where: {eventAction: 'Demo Requests'}}). */
    dataLayer_conversion_events?: unknown[];
    /** Capture outbound POST activity on selected pages for internal conversion-request discovery */
    post_request_capture?: AnalyticsPostRequestCapture;
    /** Exclude traffic from your team's IP addresses so internal visits (office, home office, VPN) don't count in your dashboard stats. */
    internal_ip_filter?: AnalyticsInternalIpFilter;
}

export interface AppearanceDisclaimersTextValue {
    /** AI disclaimer text */
    ai?: string;
    /** Powered-by text */
    poweredBy?: string;
}

export interface AppearanceDisclaimers {
    /** Always show disclaimers (not just on first message) */
    always_show?: boolean;
    /** Disclaimer text by language code */
    text?: Record<string, AppearanceDisclaimersTextValue>;
}

export interface AppearanceWidgetTheme {
    /** Main widget text color, including chat body copy, typed input text, dynamic-question pill labels, and other surfaces that already use the primary text token. */
    text_primary?: string | null;
    /** Supporting widget text/icon color for roles already using the secondary text token, such as dynamic-question arrows, follow-up/suggested-answer arrows, and supporting popup copy. Does not affect muted header icons or primary CTA text. */
    text_secondary?: string | null;
    /** Placeholder-like widget text, including search placeholder copy, minimized ask label, and the idle send-arrow state. */
    text_placeholder?: string | null;
    /** Disclaimer copy, powered-by text, and the Rose logo watermark. */
    text_disclaimer?: string | null;
    /** Neutral muted icons such as header controls, close buttons, and other idle icon roles. */
    icon_muted?: string | null;
    /** Preferred widget font family name. It must already be available on the customer website or in the browser. Falls back to the widget default font stack when unavailable. */
    font_family?: string | null;
    /** Preferred font family for conversion CTA buttons only, such as the CTA group above the search bar or inline CTA buttons in chat. Does not affect dynamic-question pills. The font must already be available on the customer website or in the browser. */
    cta_font_family?: string | null;
    /** Letter spacing for body copy, expressed as a percentage of font size. Example: 2 becomes 0.02em. */
    body_tracking_pct?: number | null;
    /** Line height for body copy, expressed as a percentage. Example: 150 becomes 150%. */
    body_leading_pct?: number | null;
    /** Letter spacing for conversion CTA button labels only, expressed as a percentage of font size. Example: 2 becomes 0.02em. */
    cta_tracking_pct?: number | null;
    /** Line height for conversion CTA button labels only, expressed as a percentage. Example: 125 becomes 125%. */
    cta_leading_pct?: number | null;
    /** Preferred font weight for regular body copy. */
    body_font_weight?: number | null;
    /** Preferred font weight for bold or emphasized body copy such as markdown **bold** text in assistant responses. Does not affect popup headings or other non-body strong UI. */
    body_strong_font_weight?: number | null;
    /** Preferred font weight for live search input text. */
    input_font_weight?: number | null;
    /** Preferred font weight for placeholder-like surfaces such as the search placeholder and minimized ask label. */
    placeholder_font_weight?: number | null;
    /** Preferred font weight for primary conversion CTA buttons, such as the first CTA shown above the search bar. When unset, those buttons keep their default weight. */
    primary_cta_font_weight?: number | null;
    /** Preferred font weight for secondary conversion CTA buttons, such as the lighter secondary button shown next to the primary CTA. When unset, those buttons keep their default weight. */
    secondary_cta_font_weight?: number | null;
    /** Rounded corner radius for outer compact glass shells such as the minimized and collapsed widget entry points. */
    compact_shell_radius?: number | null;
    /** Rounded corner radius for compact controls inside compact shells, such as the minimized ask button and the compact search input. */
    compact_control_radius?: number | null;
    /** Alpha percentage for the start stop of the widget glass effect gradient. Example: 30 matches the current 0.30 alpha. */
    glass_effect_start_alpha_pct?: number | null;
    /** Alpha percentage for the end stop of the widget glass effect gradient. Example: 18 matches the current 0.18 alpha. */
    glass_effect_end_alpha_pct?: number | null;
    /** Rounded corner radius for card-like widget surfaces such as question bubbles, popup cards, and evaluation buttons. */
    card_radius?: number | null;
    /** Rounded corner radius for conversion CTA buttons only. Dynamic-question pills and follow-up/suggested-answer pills stay on the shared pill radius. */
    cta_radius?: number | null;
}

export interface AppearanceConfig {
    /** Primary brand color (hex, e.g. #0A42C4) */
    brand_color?: string;
    /** CSS z-index for the widget */
    z_index?: number;
    /** Allow users to toggle between expanded and copilot (side panel) layouts */
    enable_copilot_layout?: boolean;
    /** Overrides the default placeholder per language code (e.g. en, fr). Leave a language out to keep the default wording for it. */
    input_placeholder?: Record<string, string>;
    /** Disclaimer display settings */
    disclaimers?: AppearanceDisclaimers;
    /** Advanced semantic styling overrides for the website widget. These fields map to existing widget theme tokens and surfaces; leave them empty to keep the system defaults. */
    widget_theme?: AppearanceWidgetTheme;
}

export interface BehaviorConfig {
    /** Normal mode produces concise, chat-style answers and uses fewer tokens — we recommend it for most use cases. Detailed mode gives fuller explanations. Long mode generates thorough responses but is significantly less efficient. */
    response_length?: 'normal' | 'detailed' | 'long';
    /** Use a preset as a starting point, then edit freely to match your brand voice. Language Architecture rules (prohibited expressions) are always enforced separately. */
    response_tone?: string;
    /** Disable this if you don't want the agent to tailor responses based on IP-based company detection (e.g., for demo environments or when company detection is unreliable). */
    use_visitor_context?: boolean;
    /** Information-first: prioritize clear, informative responses over conversion; answer thoroughly and let visitors self-serve, offering a demo only when they are clearly ready or ask for one. Conversion-first: prioritize conversion; act as an SDR that builds interest, keeps answers non-terminal, and guides qualified visitors toward a demo. */
    playbook?: 'information' | 'conversion';
    /** Interest score: offer a demo once accumulated interest points cross the threshold. Readiness judge: offer a demo only when an AI SDR sees a genuine buying signal (asked pricing, showed pull), so conversations continue longer instead of pitching too early. Note: the Conversion-first playbook always uses the Readiness judge — this setting only applies to the Information-first playbook. */
    demo_timing?: 'interest_score' | 'readiness_judge';
}

export interface ChatgptConfig {
    /** Set this only if your homepage redirects visitors to another page (e.g. a language path like /fr/) and that redirect drops URL parameters — which stops the ChatGPT conversation from reopening. Enter the exact page where Rose loads and keeps the link, for example https://payfit.com/fr/. Leave empty to use your homepage. */
    resume_landing_url?: string | null;
}

export interface ContentConfig {
    /** How many turns to wait before recommending content again. Set to 0 to allow recommendations on every turn (no cooldown). Set higher to space out recommendations. */
    cooldown_turns?: number;
}

export interface ContentGatingScenariosItem {
    /** Describe in plain language what the visitor says or asks that should make the agent offer this content. Be specific so the AI knows when to act. Example: 'Visitor mentions their industry, sector, or business type' or 'Visitor asks about pricing or costs'. */
    trigger: string;
    /** A short label for this type of content (e.g. 'case_study', 'comparison_guide', 'personalized_video'). The agent will only offer content types that appear in your scenarios — nothing else. Use the same label across scenarios that offer the same type of content. */
    content_type: string;
    /** This is the phrase the agent will use when offering content to the visitor. It appears in the message like: '💌 Would you like me to share [this phrase]?' — so write it naturally. Example: 'a case study from a company in your industry' → agent says '💌 Would you like me to share a case study from a company in your industry?'. If left empty, the agent falls back to the content_type label (e.g. 'a case_study'), which sounds robotic. */
    offer_description?: string;
    /** Enable this if the visitor is explicitly asking for this content (e.g. 'Can you send me a case study?'). When enabled, the agent skips the offer step and asks for the visitor's email right away — saving one turn in the conversation. Leave disabled for triggers where the agent should first answer the question, then propose the content. */
    direct_capture?: boolean;
}

export interface ContentGatingConfig {
    /** How many messages should the agent wait before offering content? The agent won't propose any gated content before this many exchanges, even if a trigger matches. Set higher to let the conversation warm up first. */
    gate_after_messages?: number;
    /** Each row defines a scenario: when should the agent offer content, what type, and how to describe it. The agent will ONLY offer content types listed here. Add one row per trigger/content combination. */
    scenarios?: ContentGatingScenariosItem[];
    /** If enabled, the agent will ask for the visitor's email address before sending the content. Disable only if you want to offer content without capturing a lead. */
    require_email?: boolean;
    /** If enabled, the agent will also ask for the visitor's company name in addition to their email. */
    require_company?: boolean;
}

export interface CtasCtasItem {
    /** Unique CTA identifier (lowercase, numbers, and hyphens only) */
    cta_id: string;
    /** CTA URL by language code */
    url: Record<string, string>;
    /** CTA button text by language code */
    text?: Record<string, string>;
    /** Only used when this CTA opens an in-chat booking calendar. After the visitor completes a booking inside the embedded calendar, the widget redirects them to this URL so the page's existing thank_you_pages conversion tracking and post-conversion qualification flow kick in. Leave blank to keep the visitor in chat after booking (calendar closes, no redirect). Example: https://example.com/thank-you. */
    post_booking_redirect_url?: string;
    /** Show this CTA only after an answer to a visitor message with one of these intents, in addition to the regular CTAs. Leave empty to always show it. Example: support, for a help-center CTA. */
    show_on_intents?: string[];
}

export interface CtasAliasOverridesEntryValue {
    url?: Record<string, string>;
    text?: Record<string, string>;
}

export interface CtasAdditionalCtaUrlsItem {
    /** Unique identifier for this additional CTA URL (lowercase, numbers, and hyphens only) */
    cta_id: string;
    /** Additional CTA URL by language code */
    url: Record<string, string>;
}

export interface CtasCtaClickSelectorsItem {
    /** CSS selector that matches the button, e.g. '.signup-button' or '[data-cta=free-trial]'. Find it by right-clicking the button and choosing Inspect. */
    selector: string;
    /** Optional. When several buttons share the same selector, list the button's visible text so ONLY that button is tracked. On multilingual sites, add each language's wording, e.g. 'Essai gratuit' and 'Free trial' — a click matches if the button's text contains ANY entry. Case-insensitive substring match. */
    text?: string[];
    /** A name for this CTA, shown in your analytics (e.g. 'Free trial'). Optional — falls back to the button's text. */
    label?: string;
}

export interface CtasConfig {
    /** Add one entry per CTA: a unique id, the destination URL, and the button label for each language. */
    ctas?: CtasCtasItem[];
    /** Sparse CTA URL and label overrides for configured alias domains. Managed through the curated CTA editor. */
    alias_overrides?: Record<string, Record<string, CtasAliasOverridesEntryValue>>;
    /** Where the demo CTA shows after Rose offers a demo: inside the chat (inline) or in the search-bar strip. Auto uses inline only when a single widget CTA is configured. */
    inline_cta_on_demo_mode?: 'auto' | 'always' | 'never';
    /** Legacy boolean retained for old client overrides. Use Inline demo CTA mode instead. */
    inline_cta_on_demo?: boolean;
    /** Add CTA destinations not covered by the buttons above — the address each button opens. One entry covers that button everywhere on your site. Use * as a wildcard, e.g. 'https://app.example.com/signup*'. */
    additional_cta_urls?: CtasAdditionalCtaUrlsItem[];
    /** Only for CTA buttons that navigate with JavaScript and have no real link to match above. Give a CSS selector for the button; a click on it counts as a CTA click. Selectors can break on redesigns, so prefer the destinations above for real links. Example: selector '.signup-button', name 'Free trial'. */
    cta_click_selectors?: CtasCtaClickSelectorsItem[];
    /** Counts every click on your configured CTA links (Rose CTA destinations and the Website CTA URLs above) in your analytics. Your links are left unchanged. Only real links (<a href>) are counted. */
    cta_click_tracking_enabled?: boolean;
    /** Adds Rose visitor/session IDs to the page the visitor is currently on — the address-bar URL is rewritten in place, no click needed. Use this so analytics or forms on that same page can read the IDs. */
    in_place_url_attribution_enabled?: boolean;
    /** Adds the same Rose visitor/session IDs to the links that leave your site — your configured CTA destinations (Rose CTA destinations and the Website CTA URLs) — so the IDs travel to the destination page when a visitor clicks and a conversion there can be credited to Rose. Only real links (<a href>) are decorated. */
    cta_link_attribution_enabled?: boolean;
    /** When a visitor arrives on your page with campaign or ad tags in the URL (for example utm_source, gclid, fbclid) and then clicks a CTA, Rose copies those tags onto the destination link. That way the page they land on — and your analytics or CRM — still know which campaign or ad brought them in, instead of losing that at the click. Always applies to Rose's widget CTA buttons; it also applies to your own website CTA links when Decorate CTA links above is on. */
    parameters_forwarding?: 'none' | 'allowlist' | 'all';
    /** URL parameter names to forward when Parameters forwarding is set to Only allowlist. Defaults to common attribution parameters. */
    parameters_forwarding_allowlist?: string[];
}

export interface EngagementDynamicQuestionsQuestionsValuePagesItem {
    /** URL patterns to match (glob syntax). */
    patterns?: string[];
    /** Questions to show on matching pages */
    questions?: string[];
}

export interface EngagementDynamicQuestionsQuestionsValue {
    /** Default questions shown on all pages */
    default?: string[];
    /** Page-specific question overrides (matched by URL pattern, first match wins). */
    pages?: EngagementDynamicQuestionsQuestionsValuePagesItem[];
}

export interface EngagementDynamicQuestions {
    /** Enable dynamic question generation */
    enabled?: boolean;
    /** Number of questions to display at the same time. 0 hides them. */
    number_of_questions?: number;
    /** Starter questions visitors see in the search bar */
    questions?: Record<string, EngagementDynamicQuestionsQuestionsValue>;
}

export interface EngagementSuggestedAnswers {
    /** How often the agent asks clarifying questions before answering. Never: always answers directly. Low: rarely asks. Moderate (default): asks when helpful. High: frequently asks. Very High: asks as much as needed. */
    frequency?: 'never' | 'low' | 'moderate' | 'high' | 'very_high';
    /** Show clickable answer buttons below the agent's questions so visitors can respond with one tap instead of typing */
    enabled?: boolean;
    /** Custom instructions for generating the clickable answer buttons. Example: if the agent asks 'What's your monthly traffic?', buttons could be: Under 5k | 5k-30k | 30k-100k | 100k+. Use rules like 'keep suggestions under 5 words' or 'always include a negative option'. */
    rules?: string;
}

export interface EngagementFollowupSuggestions {
    /** Enable follow-up suggestions */
    enabled?: boolean;
}

export interface EngagementConfig {
    /** Dynamic starter questions shown to visitors */
    dynamic_questions?: EngagementDynamicQuestions;
    /** Controls how the agent gathers context before answering. Frequency sets how often the agent asks clarifying follow-up questions. Suggested answers are clickable response buttons shown to visitors to guide the conversation. */
    suggested_answers?: EngagementSuggestedAnswers;
    /** Follow-up question suggestions after agent responses */
    followup_suggestions?: EngagementFollowupSuggestions;
}

export interface FormAssistantPagesItem {
    /** URL patterns to match. To match query params or hashes, include ? or # explicitly in the pattern. */
    patterns: string[];
    questions: string[];
}

export interface FormAssistantConfig {
    /** Pages where copilot mode activates, with questions to display. Most specific matching pattern wins. To match query params or hashes, include ? or # explicitly in the pattern. */
    pages?: Record<string, FormAssistantPagesItem[]>;
}

export interface GeoLongTail {
    /** Hard cap on long-tail FAQs sourced from the site-wide question pool. */
    max_global?: number;
    /** Hard cap on long-tail FAQs sourced from each per-page question pool. 0 disables per-page long-tail. */
    max_page?: number;
    /** Token-Jaccard threshold (0.0-1.0). A long-tail candidate is dropped when its similarity to any existing main FAQ title meets or exceeds this value. Higher = more permissive (more long-tail survives). */
    dedup_jaccard?: number;
}

export interface GeoQualityReview {
    /** When off, FAQs are published straight from the formatter without semantic review (legacy behaviour). */
    enabled?: boolean;
}

export interface GeoPublishing {
    /** Optional custom subdomain where the FAQ page is served (e.g. 'faq.client.com'). When set, the canonical URL becomes https://{public_host}/frequently-asked-questions and the Cloudflare Worker 301-redirects faq.userose.ai requests to it. Leave empty to serve under https://faq.userose.ai/{site_domain}/frequently-asked-questions. Requires a CNAME from {public_host} to faq.userose.ai. Mutually exclusive with public_base. Null clears a value a workspace under a path would otherwise inherit from its host. */
    public_host?: string | null;
    /** Optional full URL prefix where the FAQ page is served as a subdirectory of the client's main domain (e.g. 'https://www.client.com/faq'). When set, the canonical URL becomes {public_base}/ and the Cloudflare Worker 301-redirects faq.userose.ai requests to it. Requires the client to run a reverse proxy forwarding {public_base}/* to faq.userose.ai/{site_domain}/*. Must be https, no trailing slash, at most two path segments (e.g. 'https://www.client.com/fr/faq' for a workspace under a path). Mutually exclusive with public_host. Null clears a value a workspace under a path would otherwise inherit from its host. */
    public_base?: string | null;
    /** Public URL of the logo to show in the page header. Leave empty to omit. */
    logo_url?: string;
    /** Optional override of the default 'Frequently Asked Questions — {domain}' title. */
    title?: string;
    subtitle?: string;
    /** Heading shown above the rarer, less-clustered visitor questions. Example: 'Curious about…' or 'More questions visitors asked'. */
    long_tail_section_title?: string;
    long_tail_section_subtitle?: string;
    /** Placeholder text inside the in-page search bar that filters FAQs as visitors type. */
    search_placeholder?: string;
    background_color?: string;
    /** Card background when divider style is 'card'. */
    surface_color?: string;
    text_color?: string;
    /** Used for subtitle, answers, and footer text. */
    muted_color?: string;
    primary_color?: string;
    border_color?: string;
    /** CSS font-family for body text. */
    font_family?: string;
    /** CSS font-family for the page title and questions. */
    heading_font_family?: string;
    heading_weight?: number;
    border_radius_px?: number;
    question_size_px?: number;
    answer_size_px?: number;
    title_size_px?: number;
    max_width_px?: number;
    divider_style?: 'hairline' | 'rule' | 'card' | 'thick';
    /** Tracks which preset the page styling was last derived from. Hint only — renderer ignores this field. 'brand' means the styling was auto-derived from appearance.brand_color. */
    theme_preset?: 'brand' | 'minimal' | 'editorial' | 'corporate' | 'dark' | 'warm' | 'bold';
    /** Raw CSS appended to the page. Scoped to .geo-faq-page at render time. */
    custom_css?: string;
}

export interface GeoConfig {
    /** Let the scheduled runner regenerate and republish this domain's FAQ page on its own. Off means the page only changes when someone runs the pipeline by hand — turning the GEO feature on is enough to generate a page and review it in the backoffice. */
    scheduled_generation?: boolean;
    /** How often (in days) the scheduled runner regenerates FAQ content. Only applies when scheduled generation is on. */
    generation_cadence_days?: number;
    /** Minimum number of visitor questions required before the AI can generate FAQ content. If there aren't enough questions, the generation is skipped. */
    min_questions_threshold?: number;
    /** How many days of conversation history to analyze when gathering visitor questions. */
    lookback_days?: number;
    /** Number of most-visited pages to generate FAQ content for, ranked by question volume. */
    top_pages_count?: number;
    /** Maximum number of visitor questions to fetch from conversation history per pipeline run. Higher values improve coverage for busy domains but increase processing time. */
    max_questions?: number;
    /** Custom instructions appended to the FAQ generation prompt. Use this to control tone, style, or content rules. Example: 'Always mention that we offer a 14-day free trial. Use formal language. Never mention competitor names.' */
    faq_generation_instructions?: string;
    /** Controls for the long-tail (single-occurrence) FAQ track. Long-tail questions are visitor questions too rare to cluster, surfaced via an LLM judge that rewrites them and filters duplicates of the main FAQ list. */
    long_tail?: GeoLongTail;
    /** Semantic review of every generated FAQ before publishing. An LLM judge accepts, rewrites (twice at most) or rejects each candidate; rejected candidates are replaced by lower-ranked questions. On by default — turn it off only to reproduce the legacy unreviewed output. */
    quality_review?: GeoQualityReview;
    /** How generated FAQ pages look when published. Edited via the dedicated GEO FAQ page-style editor. */
    publishing?: GeoPublishing;
}

export interface InchatBookingConfig {
    /** When on, the chatbot won't accept personal email addresses (Gmail, Yahoo, Outlook, etc.) during booking — it asks for a work email instead. After a couple of tries it accepts whatever the visitor gives so booking is never blocked. Turn off for B2C sites where personal emails are expected. */
    require_professional_email?: boolean;
    /** Add a privacy notice or legal text. To add a link, use [link text](https://example.com). Example: 'By providing your email you agree to our [privacy policy](https://example.com/privacy).' Leave empty to show no disclaimer. */
    email_disclaimer?: Record<string, string>;
}

export interface IntegrationsCalcom {
    enabled?: boolean;
    /** Embedded shows the calendar inline in the conversation. Popup opens it as an overlay on the page. */
    mode?: 'in-chat' | 'popup';
}

export interface IntegrationsCalendly {
    enabled?: boolean;
    /** Embedded shows the calendar inline in the conversation. Popup opens it as an overlay on the page. */
    mode?: 'in-chat' | 'popup';
}

export interface IntegrationsVector {
    /** Off by default. When on, the Vector.co pixel loads on this site. Cost is per identified contact — only enable for high-priority sites. */
    enabled?: boolean;
}

export interface IntegrationsSlack {
    /** Off by default. When on, conversations are posted to the channel below. */
    enabled?: boolean;
    /** From your Slack app → OAuth & Permissions → Bot User OAuth Token. Starts with xoxb- and needs the chat:write permission. (An app-level token starting with xapp- will not work.) */
    bot_token?: string;
    /** Where conversations are posted. In Slack, open the channel → its name → copy the Channel ID at the bottom (looks like C0123ABCD). */
    channel_id?: string;
    /** Per message: every question and answer, live in the thread. Digest: one summary when the conversation ends. Qualified only: only conversations where a visitor shares an email or asks for a demo. */
    push_mode?: 'per_message' | 'digest' | 'qualified_only';
    /** Wait until a conversation reaches this many visitor messages before posting it to Slack. Keep at 1 to post from the first question; raise it to skip one-off visitors. */
    min_turns_before_push?: number;
    /** Which environments post to Slack. Staging and production (default) keeps internal test traffic out of your channel. Production only is the strictest. All also includes the internal test environment. */
    environments?: 'staging_and_production' | 'production_only' | 'all';
}

export interface IntegrationsIntercom {
    /** Off by default. When on, support-intent answers offer a link that opens your Intercom messenger on the visitor's current page. */
    enabled?: boolean;
    /** Where the messenger opens. Messages starts a new conversation, Help opens your help center, Home is the messenger landing, Tickets opens ticket status. */
    default_space?: 'home' | 'messages' | 'help' | 'tickets';
    /** Where the visitor is sent if the Intercom messenger is not loaded on the page they are on (for example your help center). Leave empty to do nothing when the messenger is unavailable. */
    fallback_url?: string;
}

export interface IntegrationsIdentifiedAccountsWebhook {
    /** Off by default. When on, each newly identified company account is sent to the URL below within a few minutes. */
    enabled?: boolean;
    /** The HTTPS address that receives each identified account. We send a POST with the company details (name, domain, sector, size, ...) as JSON. Example: https://hooks.zapier.com/hooks/catch/123/abc. */
    webhook_url?: string;
    /** A secret you choose to confirm each request really came from Rose. If set, we add a signature in the X-Webhook-Signature header that your endpoint can check. Leave empty to skip. */
    secret?: string;
    /** Off by default: every identified company is sent, even if the visitor never chats. Turn on to only send accounts whose visitor actually started a conversation with Rose — fewer accounts, but warmer ones. */
    only_with_conversation?: boolean;
}

export interface IntegrationsIdentifiedVisitorsWebhook {
    /** Off by default. When on, each newly identified person is sent to the URL below within a few minutes. Only visitors we can name are sent — a visit where we recognise the company but not the person goes to the identified accounts webhook instead. */
    enabled?: boolean;
    /** The HTTPS address that receives each identified person. We send a POST with their details (name, work email, job title, LinkedIn) plus their company and how they reached your site, as JSON. Example: https://hooks.zapier.com/hooks/catch/123/abc. */
    webhook_url?: string;
}

export interface IntegrationsScheduledExportWebhook {
    /** Off by default. When on, the previous day or week is sent to the URL below every morning or every Monday morning. */
    enabled?: boolean;
    /** The HTTPS address that receives the exports. We send one POST per dataset and period, with the same rows as the backoffice CSV export, as JSON. */
    webhook_url?: string;
    frequency?: 'daily' | 'weekly';
    /** Conversations: one row per conversation, with its transcript. Accounts: one row per company active in the period. */
    datasets?: string[];
    /** Days and weeks start at midnight in this IANA timezone (for example Europe/Paris). */
    timezone?: string;
}

export interface IntegrationsConfig {
    /** Show your Cal.com booking page from the chat. Requires In-chat booking. */
    calcom?: IntegrationsCalcom;
    /** Show your Calendly booking page from the chat. Requires In-chat booking. */
    calendly?: IntegrationsCalendly;
    /** Contact-level identification via Vector.co pixel. US traffic only. ~$0.10 per identified contact. */
    vector?: IntegrationsVector;
    /** Send conversations to a Slack channel — one thread per conversation, each visitor question and Rose answer posted as a reply inside it. */
    slack?: IntegrationsSlack;
    /** When on, Rose answers support questions by offering to open your Intercom messenger instead of handing out an email. Requires the Intercom messenger to already load on your site. */
    intercom?: IntegrationsIntercom;
    /** Send every company we identify visiting your site to your own endpoint (CRM, n8n, Zapier, ...) within a few minutes of it being identified. */
    identified_accounts_webhook?: IntegrationsIdentifiedAccountsWebhook;
    /** Send every person we identify visiting your site to your own endpoint (CRM, n8n, Zapier, ...) within a few minutes of them being identified. This is the person-level counterpart of the identified accounts webhook: a named individual rather than a company. */
    identified_visitors_webhook?: IntegrationsIdentifiedVisitorsWebhook;
    /** Receive your conversations and accounts exports on your own endpoint every day or every week, instead of downloading the CSV files from the backoffice. */
    scheduled_export_webhook?: IntegrationsScheduledExportWebhook;
}

export interface OnboardingConfig {
    /** True while the domain is in self-serve onboarding (set when it is claimed). Established domains have no override and read as live. */
    active?: boolean;
    /** The agent has been tried in the playground. */
    tested?: boolean;
    /** The Rose snippet has been installed / the site published. Once true, the account reads as live. */
    installed?: boolean;
    /** The Brand & tone screen has been reviewed and confirmed by the user. */
    brand_reviewed?: boolean;
    /** The Knowledge screen has been reviewed and confirmed by the user (they checked what Rose read). */
    knowledge_reviewed?: boolean;
    /** The Suggested questions screen has been reviewed and confirmed by the user. */
    starter_reviewed?: boolean;
    /** The CTAs screen has been reviewed and confirmed by the user. */
    cta_reviewed?: boolean;
    /** Self-serve plan selected after provisioning. Free enables the lite knowledge tier; Starter and Growth enable the advanced tier. */
    plan?: 'free' | 'starter' | 'growth';
}

export interface QualificationPersonIdentityProviders {
    /** Identify the person via RB2B (IP → hashed email → business profile). US traffic. */
    rb2b?: boolean;
    /** Identify the person via the Vector.co contact pixel. US traffic. */
    vector?: boolean;
}

export interface QualificationPersonIdentity {
    /** Off by default. When on, eligible visitors are matched to a person through the contact-level providers below. */
    enabled?: boolean;
    /** waterfall stops after the first matched provider wins. shadow_all runs all enabled providers and records each one's evidence for comparison. */
    mode?: 'waterfall' | 'shadow_all';
    /** Turn each identification vendor on or off. */
    providers?: QualificationPersonIdentityProviders;
}

export interface QualificationInterestSignalsSignalsItem {
    /** Detection criteria for the LLM */
    description?: string;
    /** Signal identifier (snake_case) */
    name?: string;
    /** Score weight (0-100) */
    points?: number;
}

export interface QualificationInterestSignals {
    /** Interest signals detected by the LLM during conversation */
    signals?: QualificationInterestSignalsSignalsItem[];
    /** Total points threshold to trigger demo proposal */
    threshold?: number | null;
}

export interface QualificationProfilingFieldDefinitionsValueOptionsItem {
    /** Fallback label kept for existing configurations. */
    label?: string;
    /** The option text shown to visitors in each configured language. */
    labels?: Record<string, string>;
    /** Stable value sent to your CRM and used in personalized URLs. It stays the same in every language. Use a short URL-safe slug with letters, digits, _ . - or ~. */
    value?: string;
}

export interface QualificationProfilingFieldDefinitionsValue {
    /** Short, readable name for this field shown in the visitor profile (e.g. 'Monthly traffic'). Leave empty to use the field's id. */
    label?: string;
    /** What this field captures (e.g. 'Company sector, revenue, structure'). Used by the AI to extract the right information from the visitor's answers. */
    description?: string;
    /** Freeform: visitor answers in their own words. Predefined: visitor picks from a list of options. */
    type?: 'freeform' | 'predefined';
    /** Custom instructions for the AI to extract the answer from conversation. Leave empty to auto-generate from the question and options. */
    extraction_prompt?: string;
    /** How strongly the agent should push to collect this field. 'normal' (default) = elicit on natural openings. 'high' = prioritize over other pending fields when picking the one question to weave per turn. 'critical' = the answer surface meaningfully depends on this field (e.g. jurisdiction, country, segment) — the agent weaves the question even on weaker openings during substantive product turns, and asks it first in the CTA gate. 'low' = only ask when there is an obvious opening, deprioritized against the others. */
    importance?: 'low' | 'normal' | 'high' | 'critical';
    /** Available choices for predefined fields. Add the text visitors see in every language configured under Brand & tone. Each translated label maps to the same stable CRM value. */
    options?: QualificationProfilingFieldDefinitionsValueOptionsItem[];
    /** Optional. If set, this field is auto-satisfied when IP enrichment already knows this visitor attribute, so the agent won't re-ask it (and CTA URLs fill from the enriched value). An explicit answer from the visitor always overrides the enriched value. */
    enrichment_field?:
        | 'company_name'
        | 'sector'
        | 'sub_sector'
        | 'domain'
        | 'email'
        | 'company_linkedin_url'
        | 'location'
        | 'employee_count'
        | 'country'
        | 'job_title'
        | 'linkedin_url';
}

export interface QualificationProfilingFormsValueFieldGroupsItemQuestionsItem {
    /** The question as the visitor will see it. Frame it so the visitor understands why answering helps them — e.g. 'What's your main use case? This helps us tailor the demo to what matters most to you.' rather than just 'What's your use case?' */
    text: string;
    /** Fields to extract from the answer. Leave empty for a purely conversational question. */
    fields?: string[];
}

export interface QualificationProfilingFormsValueFieldGroupsItem {
    /** A short label so the AI transitions naturally between topics (e.g. 'About your company', 'Your project'). */
    label: string;
    /** The questions the AI asks, in order. Frame each question around the visitor's benefit — they're sharing context so the team can help them faster. Attach fields to capture structured data for your CRM. */
    questions?: QualificationProfilingFormsValueFieldGroupsItemQuestionsItem[];
}

export interface QualificationProfilingFormsValueTriggerOnFormSubmit {
    /** Turn on to activate the qualification form from a form-submit event instead of (or in addition to) a thank-you page URL. */
    enabled: boolean;
    /** URL path patterns for the pages that contain the form we're listening to. Same syntax as Thank-You Page URLs. Only submits detected on these pages activate the qualification form. */
    pages: string[];
    /** Safety gate — only activate when the submitted form's action URL matches one of these patterns (star is a wildcard). This prevents false positives from other forms on the same page (newsletter signups, search boxes, etc.). Inspect the form in DevTools to find the exact action URL. Example: https://get.example.com/fsg — matches regardless of query string. */
    form_action_matches: string[];
}

export interface QualificationProfilingFormsValueTrigger {
    type?: 'post_conversion';
    /** URL path patterns for your post-conversion pages. Use * as wildcard (e.g. /thank-you* matches /thank-you, /thank-you-demo, etc.). The form auto-opens on these pages right after the visitor converts. */
    pages?: string[];
    /** Use this for landing pages where the form shows an inline confirmation instead of redirecting to a thank-you URL (e.g. Unbounce AJAX forms). When a form submit is detected and matches one of the configured form action patterns, the qualification form opens immediately on the same page. */
    on_form_submit?: QualificationProfilingFormsValueTriggerOnFormSubmit;
}

export interface QualificationProfilingFormsValueOnCompleteAiScoring {
    /** When turned on, every completed qualification form triggers an AI analysis before the webhook fires. The AI generates a conversation summary and a lead score that are included in the webhook data. */
    enabled?: boolean;
    /** The AI reads the full conversation and writes a summary following these instructions. The summary is sent in the webhook payload and also used as input for the scoring step. Customize to highlight what matters most to your sales team — e.g. budget, timeline, company size, or specific use case. */
    summary_prompt?: string;
    /** The AI reads the conversation summary and the collected form answers, then assigns one of four scores: hot, warm, cold, or redirect. Tailor the criteria to your sales process. Example: 'Score as hot if budget > 50k and timeline < 3 months. Score as redirect if the visitor is asking for customer support.' */
    scoring_prompt?: string;
}

export interface QualificationProfilingFormsValueOnComplete {
    /** URL to receive the collected data when the form is completed. Leave empty until the CRM webhook is wired. */
    webhook_endpoint?: string;
    /** Shared secret for HMAC-SHA256 webhook signature verification. Sent as X-Webhook-Signature header. Copy this value into your endpoint to verify webhook authenticity. */
    webhook_secret?: string;
    /** When enabled, the AI analyzes every completed conversation and produces a summary and a score (hot, warm, cold, or redirect). Both are sent in the webhook payload so your CRM or automation tool can route leads without manual review. */
    ai_scoring?: QualificationProfilingFormsValueOnCompleteAiScoring;
}

export interface QualificationProfilingFormsValue {
    /** When this form collects data from visitors. Qualification: fields are collected during the conversation before the visitor clicks a CTA. Optionally gate the CTA until fields are collected using Blocking Fields. In-Chat Booking: fields are collected by the booking assistant after the visitor clicks a CTA, alongside their email. Post-Conversion: fields are collected on a thank-you page after the visitor has converted. */
    form_type?: 'qualification' | 'in_chat_booking' | 'post_conversion';
    /** Unique form identifier. */
    id?: string;
    /** Only applies to post_conversion forms. When true, the form is hidden from visitors and only activates with ?rose_demo=1 in the URL. Ignored by the runtime on qualification and in_chat_booking forms. */
    preview?: boolean;
    /** Organize your questions into logical groups. The AI walks through them in order, transitioning naturally between topics. Keep it short — 3 to 5 questions total works best. Each answer helps your team prepare a more relevant demo or follow-up. For in-chat booking forms, only fields referenced as {{placeholders}} in the Personalized CTA URL are actually asked — list them here so the AI knows how to capture each one (description, options). Email is collected automatically. */
    field_groups?: QualificationProfilingFormsValueFieldGroupsItem[];
    /** The CTA this form is linked to. Must match a CTA ID from the CTAs section. Used by qualification forms (to gate the CTA) and in-chat booking forms (to collect fields after the CTA is clicked). */
    cta_id?: string;
    /** Field names that must be collected before the CTA is shown. The agent asks these questions before showing the call-to-action. */
    cta_blocking_fields?: string[];
    /** URL with {{field_name}} placeholders. Filled from collected fields to create a personalized CTA link. Example: https://example.com/book?size={{company_size}}&email={{email}}. For in-chat booking forms, the placeholders also drive what the booking assistant asks the visitor — alongside email, which is always collected. */
    cta_url_template?: string;
    /** URL used when not all placeholders could be filled. */
    fallback_url?: string;
    /** When to auto-open this form. Either configure thank-you page URLs, or listen for a form submission on the current page (for inline-confirmation forms that don't redirect). */
    trigger?: QualificationProfilingFormsValueTrigger;
    /** Bold heading shown above the welcome message — auto-translated to the visitor's language. Keep it short and action-oriented. Example: 'Help us prepare your demo'. */
    welcome_title?: string;
    /** The first message visitors see when the form opens — auto-translated to their language. Frame it around their benefit: a few quick questions so the team can prepare the most relevant demo or follow-up. Example: 'Thanks for booking! A few quick questions so we can tailor your demo to your exact needs.' */
    welcome_message?: string;
    /** Shown alongside the welcome message. Visitors are more likely to complete the form when they know it's quick. Example: '2 minutes', 'under 1 minute'. */
    estimated_duration?: string;
    /** Instructions for the AI when all questions are answered — it will generate a thank-you message in the visitor's language. Tell the AI what to say: who will follow up, when, and what the visitor can expect. Example: 'Thank the visitor and let them know a sales rep will reach out within 24 hours with a demo tailored to their answers.' */
    completion_message?: string;
    /** What happens when all questions are answered — send the collected data to your CRM or automation tool via webhook, and optionally let the AI score each lead automatically. */
    on_complete?: QualificationProfilingFormsValueOnComplete;
}

export interface QualificationProfiling {
    /** When enabled, qualification forms are active. */
    enabled?: boolean;
    /** Reusable data fields shared across all forms. Define what data you want to collect — the AI extracts it from the visitor's answers. */
    field_definitions?: Record<string, QualificationProfilingFieldDefinitionsValue>;
    /** Qualification forms that collect fields from visitors. Each form references fields from the Field Library above. */
    forms?: Record<string, QualificationProfilingFormsValue>;
}

export interface QualificationConfig {
    /** Identify every visitor's company as soon as they load the page, using all enrichment sources (IP lookup and third-party de-anonymization). When off, visitors are identified only once they engage with the widget (send a message). */
    enrich_all_visitors?: boolean;
    /** Rose stays visible to every visitor, but identifies nobody (company or person) until your cookie banner reports consent through window.InboundXWidget.setIdentificationConsent(true). Turn on when your banner must gate visitor identification. Consent comes from the domain's cookie banner, so set this for the whole domain, not for a path workspace. */
    identification_requires_consent?: boolean;
    /** Identify the individual person behind a visit — name, work email, role — on top of company enrichment. Scope follows Enrich All Visitors: on means every page load, off means only after the visitor engages with the widget. */
    person_identity?: QualificationPersonIdentity;
    /** Interest signal detection configuration */
    interest_signals?: QualificationInterestSignals;
    /** Collect information from visitors through forms. Define reusable fields in the Field Library, then compose forms that reference those fields. */
    profiling?: QualificationProfiling;
}

export interface StaffHoldoutOptOutConfig {
    /** Exclude this domain from the staff holdout slice. */
    opt_out?: boolean;
}

export interface SystemModel {
    /** LLM provider */
    provider: 'azure' | 'openai' | 'anthropic' | 'google' | 'cerebras' | 'groq';
    /** Model name (e.g. gpt-4.1-mini, claude-sonnet-4-5) */
    name: string;
}

export interface SystemConfig {
    /** Primary API endpoint for the website agent */
    api_endpoint?: string | null;
    /** Evaluation API endpoint (global, same for all clients) */
    evaluation_endpoint?: string;
    /** Conversation API endpoint (global, same for all clients) */
    conversation_endpoint?: string;
    /** LLM model configuration */
    model?: SystemModel | null;
    /** Lock all CLI-editable configs for this domain. When true, rose-config CLI refuses to write. */
    config_locked?: boolean;
    /** Knowledge tier. 'lite' = chunks-only retrieval, no entity graph, skips entity extraction at ingest, answers with a smaller/cheaper model. 'advanced' = full entity/relationship graph + full answer model. Required when configuring a client. */
    tier?: 'lite' | 'advanced';
    /** Website knowledge is sourced from the new scraping system. When true, the legacy Google Drive website pass is skipped during ingestion for this client. Leave false until the client's website has been re-scraped into the new system. */
    website_migrated?: boolean;
    /** Custom documents are managed in the new system (knowledge.documents), not Google Drive. When true, the Documents section is shown in the Knowledge view so the client can add and manage docs. Leave false until the client's documents have been migrated into the new system. */
    documents_migrated?: boolean;
    /** Process conversations and private content on EU (EEA + Switzerland) model deployments. Embeddings of public website content may still be processed in the US. When on, a live chat model call with no EU-eligible route fails instead of falling back outside the EU. */
    eu_only?: boolean;
}

export interface TrackingStatusTrackingInventoryItem {
    /** CTA name (e.g. 'Demo', 'Free Trial') */
    name: string;
    /** Can Rose detect a conversion when the visitor clicks the site CTA without any Rose interaction? */
    site_no_interaction_rose_tracks?: 'working' | 'not_working' | 'unknown';
    /** Can the client's analytics detect a conversion from the site CTA without any Rose interaction? */
    site_no_interaction_client_tracks?: 'working' | 'not_working' | 'unknown';
    /** Can Rose detect a conversion when the visitor clicks the site CTA after chatting with Rose? */
    site_with_interaction_rose_tracks?: 'working' | 'not_working' | 'unknown';
    /** Can the client's analytics detect a conversion from the site CTA after chatting with Rose? */
    site_with_interaction_client_tracks?: 'working' | 'not_working' | 'unknown';
    /** Can Rose detect a conversion when the visitor clicks the CTA from within the Rose widget? */
    widget_cta_rose_tracks?: 'working' | 'not_working' | 'unknown';
    /** Can the client's analytics detect a conversion from the Rose widget CTA? */
    widget_cta_client_tracks?: 'working' | 'not_working' | 'unknown';
    /** Detection strategy used (e.g. cta_page_form, thankyou_page) */
    strategy?: string | null;
    /** Notes about this CTA's tracking setup */
    notes?: string | null;
}

export interface TrackingStatusConfig {
    /** Internal staff notes about this domain's tracking setup */
    staff_notes?: string | null;
    /** Internal staff notes about this domain's traffic (volume, quality, cookie/GTM context) */
    traffic_notes?: string | null;
    /** Manual inventory of CTA conversion tracking verification */
    tracking_inventory?: TrackingStatusTrackingInventoryItem[];
}

export interface TrafficControlPageDisplayRulesItem {
    /** URL pattern (glob) */
    pattern: string;
    /** Widget display mode for matching pages */
    display: 'displayed' | 'hidden' | 'minimized';
}

export interface TrafficControlDataLayerDisplayConditionsItem {
    /** Enter the dataLayer field to read. Use a plain key for top-level values, for example countryCode. Use dot notation for nested values, for example pagePostTerms.language. */
    key: string;
    /** Enter allowed values, one per row. If the dataLayer value is a string, it must equal one of these values. If the dataLayer value is an array, any item in the array can match one of these values. */
    values: string[];
}

export interface TrafficControlSpaWidgetHandling {
    /** Re-mount the widget automatically after client-side navigation. */
    enabled?: boolean;
    /** Only apply on these URLs. Leave empty to apply everywhere. */
    url_patterns?: string[];
}

export interface TrafficControlConfig {
    /** Drag the slider or type a value to control how many visitors see the chat widget. Lowering this below 100% means only the chosen share of visitors will see the widget. */
    traffic_percentage?: number;
    /** How widget appears: normal (always visible) or hidden_zone_click (click to reveal) */
    widget_display_mode?: 'normal' | 'hidden_zone_click';
    /** Show widget on mobile devices */
    enable_mobile?: boolean;
    /** Screen width below which is considered mobile */
    mobile_breakpoint?: number;
    /** If set, the widget only appears on these URLs. Per-page display rules can still override this allowlist. To match query params or hashes, include ? or # explicitly in the pattern. */
    display_only_on_urls?: string[];
    /** Override how the widget appears on specific pages. First matching pattern wins. To match query params or hashes, include ? or # explicitly in the pattern. */
    page_display_rules?: TrafficControlPageDisplayRulesItem[];
    /** Advanced: require values already present in window.dataLayer before Rose displays. All conditions must match. Backoffice syntax: Key is a dataLayer field name or dot path such as countryCode or pagePostTerms.language. Values is the list of allowed scalar values, one value per row, such as FR or EN. */
    data_layer_display_conditions?: TrafficControlDataLayerDisplayConditionsItem[];
    /** Some websites rebuild the page when visitors navigate instead of doing a full reload. This can destroy the widget. Enable this so the widget automatically re-appears after navigation. */
    spa_widget_handling?: TrafficControlSpaWidgetHandling;
    /** Only matters when several workspaces share one website (for example acme.com/fr and acme.com/de). On: a visitor moving to another workspace starts that workspace's own conversation. Off: the visitor keeps the same conversation across all workspaces of the website. */
    conversation_per_workspace?: boolean;
}

export type WebmcpConfig = Record<string, never>;

export interface InternalIpFilterExcludedIpsItem {
    /** Short name to remember this IP (e.g. 'Benoit home'). */
    label: string;
    /** IPv4 or IPv6 address. Paste the IP exactly as it appears (e.g. 203.0.113.42 or 2001:db8::1). */
    ip: string;
}

export interface InternalIpFilterConfig {
    /** When on, visits from the IPs below are excluded from every client's dashboard stats. Turn off to temporarily include them again without losing the list. */
    enabled?: boolean;
    /** One IP per row. Add a label so you remember who each one belongs to (e.g. 'Benoit home VPN', 'Paris office'). IPv4 and IPv6 are both accepted. For ranges, list each IP individually. */
    excluded_ips?: InternalIpFilterExcludedIpsItem[];
}

// Default values (factory seed from schema files)
export const AGENTS_DEFAULTS: AgentsConfig = {
    agents: {
        'web-agent': {
            description: 'Engages visitors on your website through chat & AI sections.',
            name: 'Website Agent',
            status: 'active',
            enabled: true,
        },
        'nurturing-agent': {
            description: 'Follows up by email based on conversation history.',
            name: 'Nurturing Agent',
            status: 'beta',
            enabled: false,
        },
        'crm-agent': {
            description: 'Hands off hot leads to sales with conversation context.',
            name: 'Handoff Agent',
            status: 'beta',
            enabled: false,
        },
        'contact-agent': {
            description: 'Proactive outbound to qualified visitors on intent signals.',
            name: 'Outreach Agent',
            status: 'beta',
            enabled: false,
        },
        'content-agent': {
            description: 'Optimizes content for AI search engines (Perplexity, ChatGPT).',
            name: 'GEO Agent',
            status: 'beta',
            enabled: false,
        },
        'llm-agent': {
            description: 'Surface your brand in ChatGPT via custom GPT & actions.',
            name: 'ChatGPT Agent',
            status: 'beta',
            enabled: false,
        },
    },
} as const;

export const BACKOFFICE_DEFAULTS: BackofficeConfig = {
    auto_translate_conversations: true,
} as const;

export const COMPETITORS_DEFAULTS: CompetitorsConfig = {
    names: [],
    profiles: {},
    battlecard_mode: true,
} as const;

export const IDENTITY_DEFAULTS: IdentityConfig = {
    description: '',
    company_name: '',
    website_url: '',
    logo_url: null,
    display_name: '',
    default_language: 'fr',
    llm_response_language: 'website_language',
    additional_languages: [],
} as const;

export const AI_SECTIONS_DEFAULTS: AiSectionsConfig = {
    enable_mobile: true,
} as const;

export const ANALYTICS_DEFAULTS: AnalyticsConfig = {
    session_utm_name: 'rose_session_id',
    visitor_utm_name: 'rose_visitor_id',
    login_urls: [],
    taxonomy: {
        topics: [
            {
                id: 'pricing',
                name: 'Pricing & Plans',
                description: 'Questions about pricing, costs, plans, discounts, or billing',
            },
            {
                id: 'demo-request',
                name: 'Demo Request',
                description: 'Requests for a product demo, trial, or live walkthrough',
            },
            {
                id: 'product-capabilities',
                name: 'Product Capabilities',
                description: 'Questions about features, functionality, and what the product can do',
            },
            {
                id: 'use-cases',
                name: 'Use Cases',
                description:
                    'Questions about how the product applies to specific industries, roles, or scenarios',
            },
            {
                id: 'getting-started',
                name: 'Getting Started',
                description: 'Questions about onboarding, setup, implementation, or first steps',
            },
            {
                id: 'integration',
                name: 'Integrations & Technical Fit',
                description:
                    'Questions about integrations, APIs, compatibility, and technical requirements',
            },
            {
                id: 'competitors',
                name: 'Competitor Comparison',
                description: 'Comparisons with alternative solutions or competitors',
            },
            {
                id: 'roi-value',
                name: 'ROI & Business Value',
                description:
                    'Questions about return on investment, business impact, and value proposition',
            },
            {
                id: 'security-compliance',
                name: 'Security & Compliance',
                description:
                    'Questions about security practices, certifications, data privacy, and regulatory compliance',
            },
            {
                id: 'enterprise',
                name: 'Enterprise Needs',
                description:
                    'Enterprise-specific questions about scale, SLAs, dedicated support, or custom solutions',
            },
            {
                id: 'support-issue',
                name: 'Support Issue',
                description: 'Existing customer support requests, bug reports, or troubleshooting',
            },
            {
                id: 'partnership',
                name: 'Partnership Inquiry',
                description: 'Partnership, reseller, or business development inquiries',
            },
            {
                id: 'other',
                name: 'Other',
                description: 'Topics that do not fit any other category',
            },
            {
                id: 'spam',
                name: 'Spam',
                description: 'Irrelevant, automated, or spam messages',
            },
        ],
    },
    form_tracking_pages: [],
    thank_you_pages: [],
    conversion_filters: {
        form_id_whitelist: [],
        form_action_whitelist: [],
        url_include: [],
        url_exclude: [],
    },
    intent_pages: {},
    stats_valid_from: null,
    posthog_url: null,
    page_load_event_suppression: 'out_of_scope_hosts',
    form_detection_strategies: {
        superform: true,
        hubspot: true,
        cta_page_form: true,
        thankyou_page: true,
        network_observer: true,
        dom_observer: false,
        dom_observer_track_wrapped_forms: false,
        post_message: true,
        data_layer: true,
    },
    dataLayer_conversion_events: [],
    parent_window_events: {
        enabled: true,
    },
    post_request_capture: {
        enabled: false,
        redact_payload_values: true,
        page_patterns: [],
    },
    internal_ip_filter: {
        enabled: false,
        excluded_ips: [],
    },
} as const;

export const APPEARANCE_DEFAULTS: AppearanceConfig = {
    brand_color: '#0A42C3',
    z_index: 9999,
    enable_copilot_layout: true,
    input_placeholder: {
        en: 'Ask anything about {{company_name}}',
        fr: 'Posez-nous vos questions sur {{company_name}}',
        es: 'Preg\u00fantanos sobre {{company_name}}',
        it: 'Chiedici di {{company_name}}',
        de: 'Frag uns zu {{company_name}}',
        pt: 'Pergunte qualquer coisa sobre {{company_name}}',
        bg: '\u041f\u043e\u043f\u0438\u0442\u0430\u0439\u0442\u0435 \u0432\u0441\u0438\u0447\u043a\u043e \u0437\u0430 {{company_name}}',
        nl: 'Vraag alles over {{company_name}}',
        ro: '\u00centreba\u021bi orice despre {{company_name}}',
        hu: 'K\u00e9rdezzen b\u00e1rmit a(z) {{company_name}} v\u00e1llalatr\u00f3l',
    },
    disclaimers: {
        always_show: false,
        text: {
            en: {
                ai: 'AI-generated replies',
                poweredBy: 'Powered by',
            },
            fr: {
                ai: 'R\u00e9ponses g\u00e9n\u00e9r\u00e9es par IA',
                poweredBy: 'Propuls\u00e9 par',
            },
        },
    },
    widget_theme: {
        text_primary: null,
        text_secondary: null,
        text_placeholder: null,
        text_disclaimer: null,
        icon_muted: null,
        font_family: null,
        cta_font_family: null,
        body_tracking_pct: null,
        body_leading_pct: null,
        cta_tracking_pct: null,
        cta_leading_pct: null,
        body_font_weight: null,
        body_strong_font_weight: null,
        input_font_weight: null,
        placeholder_font_weight: null,
        primary_cta_font_weight: null,
        secondary_cta_font_weight: null,
        compact_shell_radius: null,
        compact_control_radius: null,
        glass_effect_start_alpha_pct: null,
        glass_effect_end_alpha_pct: null,
        card_radius: null,
        cta_radius: null,
    },
} as const;

export const BEHAVIOR_DEFAULTS: BehaviorConfig = {
    response_length: 'normal',
    use_visitor_context: true,
    playbook: 'information',
    demo_timing: 'interest_score',
    response_tone:
        '- **Natural, human, conversational** tone that remains professional\n- **Clear, factual and confident** but never robotic or scripted\n- **Sound like a knowledgeable colleague** explaining how something works\n- **Do not oversell or exaggerate** but convince with accurate facts, benefits and examples\n- **Be neutral, factual and nuanced** - neutrality is more compelling and builds trust\n- **Avoid over-enthusiastic language** - don\'t use "Amazing", "Fantastic", "Excellent", "Absolutely", "Love"\n- **Don\'t use jargon or specialist language** - rephrase it so it\'s clear and easy for anyone to understand\n- **You\'re in a conversation**, not just answering isolated questions',
} as const;

export const CHATGPT_DEFAULTS: ChatgptConfig = {
    resume_landing_url: null,
} as const;

export const CONTENT_DEFAULTS: ContentConfig = {
    cooldown_turns: 0,
} as const;

export const CONTENT_GATING_DEFAULTS: ContentGatingConfig = {
    gate_after_messages: 2,
    scenarios: [
        {
            trigger: 'Visitor mentions their industry, sector, or vertical',
            content_type: 'case_study',
            offer_description: 'a case study from a company in your industry',
        },
        {
            trigger: 'Visitor asks about ROI, business results, or metrics',
            content_type: 'case_study',
            offer_description: 'a case study with detailed ROI breakdown',
        },
        {
            trigger: 'Visitor asks about existing customers, references, or social proof',
            content_type: 'case_study',
            offer_description: 'a relevant customer case study',
        },
        {
            trigger: 'Visitor compares to competitors or alternatives',
            content_type: 'comparison_guide',
            offer_description: 'a detailed comparison document',
        },
        {
            trigger: 'Visitor shares qualifying information (company size, budget, timeline)',
            content_type: 'case_study',
            offer_description: 'a case study from a similar company',
        },
    ],
    require_email: true,
    require_company: false,
} as const;

export const CTAS_DEFAULTS: CtasConfig = {
    inline_cta_on_demo_mode: 'auto',
    ctas: [],
    alias_overrides: {},
    additional_cta_urls: [],
    cta_click_tracking_enabled: true,
    cta_link_attribution_enabled: false,
    in_place_url_attribution_enabled: false,
    parameters_forwarding: 'allowlist',
    parameters_forwarding_allowlist: [
        'utm_source',
        'utm_medium',
        'utm_campaign',
        'utm_term',
        'utm_content',
        'utm_id',
        'gclid',
        'gclsrc',
        'gbraid',
        'wbraid',
        'gad_source',
        'gad_campaignid',
        'dclid',
        'fbclid',
        'msclkid',
        'li_fat_id',
        'ttclid',
        'twclid',
        'ScCid',
        'epik',
    ],
} as const;

export const ENGAGEMENT_DEFAULTS: EngagementConfig = {
    dynamic_questions: {
        enabled: true,
        number_of_questions: 2,
        questions: {
            en: {
                default: [
                    'What does {{company_name}} do?',
                    'Do you have case studies in my industry?',
                    'How much does {{company_name}} cost?',
                    'Which ROI can I expect?',
                    'How long does it take to implement {{company_name}}?',
                ],
            },
            fr: {
                default: [
                    'Quels sont les services propos\u00e9s par {{company_name}} ?',
                    'Avez-vous des cas clients dans mon secteur ?',
                    'Combien co\u00fbte {{company_name}} ?',
                    "\u00c0 quel ROI puis-je m'attendre ?",
                    "Combien de temps prend l'impl\u00e9mentation de {{company_name}} ?",
                ],
            },
        },
    },
    suggested_answers: {
        frequency: 'moderate',
        enabled: true,
    },
    followup_suggestions: {
        enabled: true,
    },
} as const;

export const GEO_DEFAULTS: GeoConfig = {
    scheduled_generation: false,
    generation_cadence_days: 7,
    min_questions_threshold: 20,
    lookback_days: 30,
    top_pages_count: 5,
    max_questions: 1000,
    long_tail: {
        max_global: 20,
        max_page: 0,
        dedup_jaccard: 0.8,
    },
    quality_review: {
        enabled: true,
    },
    publishing: {
        background_color: '#ffffff',
        surface_color: '#ffffff',
        text_color: '#111111',
        muted_color: '#6b7280',
        primary_color: '#111111',
        border_color: '#e5e7eb',
        font_family: '"Geist", -apple-system, BlinkMacSystemFont, sans-serif',
        heading_font_family: '"Geist", -apple-system, BlinkMacSystemFont, sans-serif',
        heading_weight: 600,
        border_radius_px: 6,
        question_size_px: 17,
        answer_size_px: 15,
        title_size_px: 40,
        max_width_px: 720,
        divider_style: 'hairline',
        theme_preset: 'minimal',
    },
} as const;

export const INCHAT_BOOKING_DEFAULTS: InchatBookingConfig = {
    require_professional_email: true,
} as const;

export const INTEGRATIONS_DEFAULTS: IntegrationsConfig = {
    calcom: {
        enabled: false,
        mode: 'in-chat',
    },
    calendly: {
        enabled: false,
        mode: 'in-chat',
    },
    vector: {
        enabled: false,
    },
    slack: {
        enabled: false,
        bot_token: '',
        channel_id: '',
        push_mode: 'per_message',
        min_turns_before_push: 1,
    },
    intercom: {
        enabled: false,
        default_space: 'messages',
        fallback_url: '',
    },
    identified_accounts_webhook: {
        enabled: false,
        webhook_url: '',
        secret: '',
        only_with_conversation: false,
    },
    identified_visitors_webhook: {
        enabled: false,
        webhook_url: '',
    },
    scheduled_export_webhook: {
        enabled: false,
        webhook_url: '',
        frequency: 'weekly',
        datasets: ['conversations', 'accounts'],
        timezone: 'Europe/Paris',
    },
} as const;

export const ONBOARDING_DEFAULTS: OnboardingConfig = {
    active: false,
    tested: false,
    installed: false,
    plan: 'free',
} as const;

export const QUALIFICATION_DEFAULTS: QualificationConfig = {
    enrich_all_visitors: false,
    identification_requires_consent: false,
    person_identity: {
        enabled: false,
        mode: 'shadow_all',
        providers: {
            rb2b: true,
            vector: true,
        },
    },
    interest_signals: {
        signals: [
            {
                name: 'explicit_demo_request',
                points: 21,
                description:
                    "Visitor explicitly asks for a demo, to speak with sales, or to be contacted. Triggers: 'I want to talk to sales', 'Can I talk to sales?', 'I want a demo', 'Can I book a call?', 'Can I try?', 'I want to be contacted'. Strongest buying signal.",
            },
            {
                name: 'pricing_request',
                points: 6,
                description:
                    "Visitor asks about pricing or cost. Triggers: 'How much does it cost?', 'What is the price?', 'Price', 'I want a price range', 'Is it free?', 'What's your pricing', 'Do you have a price grid?', 'Can I see pricing?'. Visitor must explicitly ask about price/cost.",
            },
            {
                name: 'visual_demo_needs',
                points: 6,
                description:
                    "Visitor expresses need to see the product visually. Includes: requests to 'see', 'show me', 'examples', 'can you demonstrate', UI questions, questions about UI/dashboard/interface/reports. Visitor must explicitly ask to SEE or be SHOWN something.",
            },
            {
                name: 'roi_business_value',
                points: 4,
                description:
                    'Visitor asks about business outcomes, ROI, or implementation. Includes: revenue impact/ROI/business results questions, success metrics, implementation timeline, onboarding or training process questions. Must ask about business outcomes, not just features.',
            },
            {
                name: 'competitor_comparison',
                points: 4,
                description:
                    'Visitor explicitly compares the product with competitors or alternatives. Shows active evaluation phase. Visitor must name a specific competitor or alternative tool.',
            },
            {
                name: 'case_study_request',
                points: 4,
                description:
                    'Visitor explicitly asks for case studies, customer examples, success stories, or proof of results. Shows need for social proof and validation before purchase decision.',
            },
            {
                name: 'technical_evaluation',
                points: 4,
                description:
                    'Visitor asks technical questions showing deeper evaluation. Includes: technical questions with depth, increasingly specific/advanced questions, questions about specific integrations or compatibility, technical implementation details (APIs, SDKs, architecture).',
            },
            {
                name: 'use_case_sharing',
                points: 4,
                description:
                    'Visitor shares specific business context, pain, or objectives. Includes: business context, pain/frustration, urgency/timeline, workflow description, industry mention, monthly traffic shared, clear objectives/KPIs. Visitor must SAY/SHARE the context explicitly.',
            },
            {
                name: 'solution_complexity',
                points: 4,
                description:
                    'Visitor discusses complex solution needs or sophisticated requirements. Includes: multiple complex requirements across platforms, multiple distinct use cases requiring custom architecture, complex multi-system integrations or technical constraints.',
            },
            {
                name: 'generic_information',
                points: 2,
                description:
                    "Visitor asks basic informational questions showing discovery/awareness phase (TOFU). Triggers: 'What is [product]?', 'What do you do?', 'Do you have [basic feature]?'. Must be genuine discovery questions.",
            },
            {
                name: 'brief_answer',
                points: 2,
                description:
                    "Visitor provides a very short or minimal response that answers a question but adds no meaningful context or buying signal. Includes one-word or low-information replies such as 'yes', 'no', 'ok', 'sure', email address, short confirmations.",
            },
            {
                name: 'off_topic',
                points: 0,
                description:
                    'Message is off-topic, spam, testing, customer support request, or completely unrelated to new product evaluation. Default for garbage messages and support requests.',
            },
        ],
        threshold: 21,
    },
    profiling: {
        enabled: false,
        field_definitions: {},
        forms: {},
    },
} as const;

export const STAFF_HOLDOUT_OPT_OUT_DEFAULTS: StaffHoldoutOptOutConfig = {
    opt_out: false,
} as const;

export const SYSTEM_DEFAULTS: SystemConfig = {
    api_endpoint: null,
    evaluation_endpoint: 'https://inboundx.app.n8n.cloud/webhook/evaluation-prod',
    conversation_endpoint: 'https://inboundx.app.n8n.cloud/webhook/final-prod',
    model: null,
    config_locked: false,
    website_migrated: false,
    documents_migrated: false,
    eu_only: false,
} as const;

export const TRACKING_STATUS_DEFAULTS: TrackingStatusConfig = {
    staff_notes: null,
    traffic_notes: null,
    tracking_inventory: [],
} as const;

export const TRAFFIC_CONTROL_DEFAULTS: TrafficControlConfig = {
    traffic_percentage: 100,
    widget_display_mode: 'normal',
    enable_mobile: false,
    mobile_breakpoint: 768,
    page_display_rules: [],
    display_only_on_urls: [],
    data_layer_display_conditions: [],
    spa_widget_handling: {
        enabled: false,
        url_patterns: [],
    },
    conversation_per_workspace: true,
} as const;

export const INTERNAL_IP_FILTER_DEFAULTS: InternalIpFilterConfig = {
    enabled: false,
    excluded_ips: [],
} as const;
